import React, {CSSProperties, useState, useRef, useEffect, useLayoutEffect} from "react"
import Icon from "../Icon/Icon"
import {b64DecodeArrayBuffer} from "../../utilities/helpers"
import Skeleton from "react-loading-skeleton"
import {useModal} from "react-modal-hook"
import checkFrontNoCheck from "../../images/check-front-no-check.svg"
import checkBackNoCheck from "../../images/check-back-no-check.svg"
import {BackCheckImageModal, FrontCheckImageModal} from "./CheckImageModal"
import classNames from "classnames"
import {ImageTransformControls} from "../ImageTransformControls/ImageTransformControls"
import {useImageToolbar} from "../../hooks/useImageToolbar"
import {detectRotationFromReference, RotationDegrees} from "../../utilities/imageOrientation"
import {CheckImageVersion} from "../../resources/checkPayments"
import {isCheckImageAvailable, isCheckPaymentVersionAvailable} from "../../hooks/useCheckPaymentImages"
import {usePrintedCheckCroppedLinks} from "../../hooks/usePrintedCheckCroppedLinks"
import ReactTooltip from "react-tooltip"

const UNAVAILABLE_IMAGE_TOOLTIP = "This image is currently unavailable"

enum Side {
	front = "Flip to Front",
	back = "Flip to Back",
	frontTiff = "Flip to Front TIFF",
	backTiff = "Flip to Back TIFF",
}

interface CheckImageProps {
	side: Side
	styles?: CSSProperties
	className?: string
	image?: ArrayBuffer | null
	imageLink: string
	withRef?: boolean
	imgRef: React.RefObject<HTMLImageElement>
	originated?: boolean
}

const CheckImage = ({
	side,
	styles,
	className,
	image,
	imageLink,
	withRef = false,
	imgRef,
	originated,
}: CheckImageProps) => {
	const sideText = side == Side.front ? "front" : "back"
	const emptyImage = side === Side.front ? checkFrontNoCheck : checkBackNoCheck

	switch (image) {
		case undefined:
			return (
				<div className={className}>
					<Skeleton />
				</div>
			)
		case null:
			return (
				<div className="no-check-placeholder">
					<p>
						{" "}
						{originated
							? "Images will be available once the check is deposited"
							: `There is no ${sideText} image to show.`}{" "}
					</p>{" "}
					<br /> <img style={styles} className={className} src={emptyImage} alt={side} />
				</div>
			)
		default:
			return (
				<img
					ref={withRef ? imgRef : undefined}
					style={styles}
					className={className}
					src={imageLink}
					alt={side}
					draggable={false}
				/>
			)
	}
}

interface CheckImageCardProps {
	frontImage?: ArrayBuffer | null
	backImage?: ArrayBuffer | null
	frontTiffImage?: ArrayBuffer | null
	backTiffImage?: ArrayBuffer | null
	frontPrintedImage?: ArrayBuffer | null
	backPrintedImage?: ArrayBuffer | null
	frontDepositedImage?: ArrayBuffer | null
	backDepositedImage?: ArrayBuffer | null
	imageVersionMode?: boolean
	id: string
	status: string
	originated?: boolean
	compact?: boolean
	expectsTiff?: boolean
}

export function CheckImageCard({
	frontImage,
	backImage,
	frontTiffImage,
	backTiffImage,
	frontPrintedImage,
	backPrintedImage,
	frontDepositedImage,
	backDepositedImage,
	imageVersionMode = false,
	id,
	status,
	originated,
	compact = false,
	expectsTiff = false,
}: CheckImageCardProps) {
	const [imageVersion, setImageVersion] = useState(CheckImageVersion.Deposited)
	const defaultVersionSetRef = useRef(false)
	const allVersionImagesSettled =
		imageVersionMode &&
		[frontPrintedImage, backPrintedImage, frontDepositedImage, backDepositedImage].every((image) => image !== undefined)
	const printedAvailable = isCheckPaymentVersionAvailable(frontPrintedImage, backPrintedImage)
	const depositedAvailable = isCheckPaymentVersionAvailable(frontDepositedImage, backDepositedImage)

	useEffect(() => {
		defaultVersionSetRef.current = false
	}, [id])

	useEffect(() => {
		if (!allVersionImagesSettled || defaultVersionSetRef.current) return
		defaultVersionSetRef.current = true
		if (depositedAvailable) {
			setImageVersion(CheckImageVersion.Deposited)
		} else if (printedAvailable) {
			setImageVersion(CheckImageVersion.Printed)
		}
	}, [allVersionImagesSettled, depositedAvailable, printedAvailable, id])

	const activeFrontImage = imageVersionMode
		? imageVersion === CheckImageVersion.Deposited
			? frontDepositedImage
			: frontPrintedImage
		: frontImage
	const activeBackImage = imageVersionMode
		? imageVersion === CheckImageVersion.Deposited
			? backDepositedImage
			: backPrintedImage
		: backImage

	const frontImageLink = activeFrontImage ? `data:image/png;base64, ${b64DecodeArrayBuffer(activeFrontImage)}` : ""
	const backImageLink = activeBackImage ? `data:image/png;base64,  ${b64DecodeArrayBuffer(activeBackImage)}` : ""
	const frontPrintedRawLink =
		frontPrintedImage && isCheckImageAvailable(frontPrintedImage)
			? `data:image/png;base64, ${b64DecodeArrayBuffer(frontPrintedImage)}`
			: ""
	const backPrintedRawLink =
		backPrintedImage && isCheckImageAvailable(backPrintedImage)
			? `data:image/png;base64, ${b64DecodeArrayBuffer(backPrintedImage)}`
			: ""
	const showPrintedCrop = imageVersionMode && imageVersion === CheckImageVersion.Printed
	const {
		front: croppedPrintedFront,
		back: croppedPrintedBack,
		isLoading: isPrintedCropLoading,
	} = usePrintedCheckCroppedLinks(showPrintedCrop ? frontPrintedRawLink : "", showPrintedCrop ? backPrintedRawLink : "")
	const resolvedFrontLink = showPrintedCrop ? croppedPrintedFront : frontImageLink
	const resolvedBackLink = showPrintedCrop ? croppedPrintedBack : backImageLink
	const isPrintedCropPending = (targetSide: Side) => {
		if (!showPrintedCrop || (targetSide !== Side.front && targetSide !== Side.back)) return false
		const rawLink = targetSide === Side.front ? frontPrintedRawLink : backPrintedRawLink
		if (!rawLink) return false
		const croppedLink = targetSide === Side.front ? croppedPrintedFront : croppedPrintedBack
		return isPrintedCropLoading || !croppedLink
	}
	const displayFrontImage = isPrintedCropPending(Side.front) ? undefined : activeFrontImage
	const displayBackImage = isPrintedCropPending(Side.back) ? undefined : activeBackImage
	const displayFrontLink = isPrintedCropPending(Side.front) ? "" : resolvedFrontLink
	const displayBackLink = isPrintedCropPending(Side.back) ? "" : resolvedBackLink
	const frontTiffImageLink = frontTiffImage ? `data:image/png;base64, ${b64DecodeArrayBuffer(frontTiffImage)}` : ""
	const backTiffImageLink = backTiffImage ? `data:image/png;base64, ${b64DecodeArrayBuffer(backTiffImage)}` : ""
	const imgRef = useRef<HTMLImageElement>(null)
	const [side, setSide] = useState(Side.front)
	const [orientationMap, setOrientationMap] = useState<Partial<Record<Side, boolean>>>({})
	const [detectedRotationMap, setDetectedRotationMap] = useState<Partial<Record<Side, RotationDegrees>>>({})
	const [detectionFailedSet, setDetectionFailedSet] = useState<Set<Side>>(new Set())
	const userRotatedRef = useRef(false)

	useEffect(() => {
		setOrientationMap({})
		setDetectedRotationMap({})
		setDetectionFailedSet(new Set())
		userRotatedRef.current = false
	}, [id, imageVersion])

	useEffect(() => {
		const links: Array<[Side, string]> = [
			[Side.front, resolvedFrontLink],
			[Side.back, resolvedBackLink],
			[Side.frontTiff, frontTiffImageLink],
			[Side.backTiff, backTiffImageLink],
		]
		links.forEach(([imgSide, link]) => {
			if (!link) return
			const img = new Image()
			img.onload = () => {
				setOrientationMap((prev) => ({...prev, [imgSide]: img.naturalWidth >= img.naturalHeight}))
			}
			img.src = link
		})
	}, [resolvedFrontLink, resolvedBackLink, frontTiffImageLink, backTiffImageLink])

	// When both the Image and its TIFF are available, detect the rotation needed
	// for the Image to match the TIFF (which is reliably oriented horizontally).
	useEffect(() => {
		const pairs: Array<[Side, string, string]> = [
			[Side.front, resolvedFrontLink, frontTiffImageLink],
			[Side.back, resolvedBackLink, backTiffImageLink],
		]
		pairs.forEach(([imgSide, imageLink, tiffLink]) => {
			if (!imageLink || !tiffLink) return
			detectRotationFromReference(imageLink, tiffLink)
				.then((detected) => {
					setDetectedRotationMap((prev) => ({...prev, [imgSide]: detected}))
				})
				.catch(() => {
					// Signal failure so expectedAutoRotation falls back to orientation-based logic
					// instead of waiting indefinitely.
					setDetectionFailedSet((prev) => {
						const next = new Set(prev)
						next.add(imgSide)
						return next
					})
				})
		})
	}, [resolvedFrontLink, resolvedBackLink, frontTiffImageLink, backTiffImageLink])

	const isHorizontalImage = orientationMap[side] ?? true
	const {zoom, rotation, transformOrigin, transform, aspectRatio} = useImageToolbar(
		imgRef,
		isHorizontalImage,
		`${side}-${imageVersion}`
	)
	const withZoom = zoom.val > 1

	const sideImageLink = (() => {
		switch (side) {
			case Side.front:
				return displayFrontLink
			case Side.back:
				return displayBackLink
			case Side.frontTiff:
				return frontTiffImageLink
			case Side.backTiff:
				return backTiffImageLink
		}
	})()

	const expectedAutoRotation: RotationDegrees | undefined = (() => {
		if (side === Side.frontTiff || side === Side.backTiff) return 0
		if (!sideImageLink) return undefined
		// Lob printed checks are rendered upright on A4; portrait page dimensions must not trigger deposited-scan rotation.
		if (showPrintedCrop) return 0
		const detected = detectedRotationMap[side]
		if (detected !== undefined) return detected
		if (expectsTiff && !detectionFailedSet.has(side)) {
			const sideTiffImage = side === Side.front ? frontTiffImage : backTiffImage
			if (sideTiffImage !== null) return undefined
		}
		const sideOrientation = orientationMap[side]
		if (sideOrientation === undefined) return undefined
		return sideOrientation ? 0 : 90
	})()

	// Resolve the auto-rotation for any side so the fullscreen modal can open images
	// in the same orientation as the auto-rotated thumbnail. Unlike expectedAutoRotation,
	// this never returns undefined so it is always usable as a modal's initial rotation.
	const resolveSideRotation = (targetSide: Side): RotationDegrees => {
		if (targetSide === Side.frontTiff || targetSide === Side.backTiff) return 0
		if (showPrintedCrop) return 0
		const detected = detectedRotationMap[targetSide]
		if (detected !== undefined) return detected
		return orientationMap[targetSide] === false ? 90 : 0
	}
	const frontAutoRotation = resolveSideRotation(Side.front)
	const backAutoRotation = resolveSideRotation(Side.back)

	useLayoutEffect(() => {
		userRotatedRef.current = false
	}, [side, imageVersion])

	useLayoutEffect(() => {
		if (userRotatedRef.current) return
		if (expectedAutoRotation === undefined) return
		rotation.setRotation(expectedAutoRotation)
	}, [expectedAutoRotation, rotation])

	const handleRotate = () => {
		userRotatedRef.current = true
		rotation.changeRotation()
	}

	const isImageReady =
		!sideImageLink ||
		userRotatedRef.current ||
		(expectedAutoRotation !== undefined && expectedAutoRotation === rotation.val)

	const inlineImgStyle = {
		"--img-transform-origin": transformOrigin,
		"--img-transform": transform,
		"--img-aspect-ratio": aspectRatio,
		visibility: isImageReady ? undefined : ("hidden" as const),
	} as CSSProperties

	const changeSide = () => {
		switch (side) {
			case Side.front:
				setSide(Side.back)
				break
			case Side.back:
				setSide(Side.front)
				break
			case Side.frontTiff:
				setSide(Side.backTiff)
				break
			case Side.backTiff:
				setSide(Side.frontTiff)
				break
		}
	}

	const baseImageProps = {styles: inlineImgStyle, imgRef: imgRef, originated}
	const frontImageProps = {
		side: Side.front,
		className: "check-front-image",
		image: displayFrontImage,
		imageLink: displayFrontLink,
		...baseImageProps,
	}
	const backImageProps = {
		side: Side.back,
		className: "check-back-image",
		image: displayBackImage,
		imageLink: displayBackLink,
		...baseImageProps,
	}

	const frontImageObject = <CheckImage key={`front-${imageVersion}`} {...frontImageProps} withRef={true} />
	const backImageObject = <CheckImage key={`back-${imageVersion}`} {...backImageProps} withRef={true} />

	const frontImageForModal = <CheckImage key={`front-modal-${imageVersion}`} {...frontImageProps} />
	const backImageForModal = <CheckImage key={`back-modal-${imageVersion}`} {...backImageProps} />

	const frontTiffImageObject = frontTiffImageLink ? (
		<img
			key="frontTiff"
			ref={imgRef}
			style={inlineImgStyle}
			className="check-front-image"
			src={frontTiffImageLink}
			alt={Side.frontTiff}
			draggable={false}
		/>
	) : (
		frontImageObject
	)
	const backTiffImageObject = backTiffImageLink ? (
		<img
			key="backTiff"
			ref={imgRef}
			style={inlineImgStyle}
			className="check-back-image"
			src={backTiffImageLink}
			alt={Side.backTiff}
			draggable={false}
		/>
	) : (
		backImageObject
	)

	const frontTiffImageForModal = frontTiffImageLink ? (
		<img
			key="frontTiff"
			style={inlineImgStyle}
			className="check-front-image"
			src={frontTiffImageLink}
			alt={Side.frontTiff}
			draggable={false}
		/>
	) : (
		frontImageForModal
	)
	const backTiffImageForModal = backTiffImageLink ? (
		<img
			key="backTiff"
			style={inlineImgStyle}
			className="check-back-image"
			src={backTiffImageLink}
			alt={Side.backTiff}
			draggable={false}
		/>
	) : (
		backImageForModal
	)

	const [showFrontImage, hideFrontImage] = useModal(
		() => (
			<FrontCheckImageModal
				id={id}
				status={status}
				frontImage={frontImageForModal}
				backImage={backImageForModal}
				closeModal={hideFrontImage}
				initialRotations={[frontAutoRotation, backAutoRotation]}
			/>
		),
		[frontImageForModal, frontAutoRotation, backAutoRotation]
	)

	const [showBackImage, hideBackImage] = useModal(
		() => (
			<BackCheckImageModal
				id={id}
				status={status}
				frontImage={frontImageForModal}
				backImage={backImageForModal}
				closeModal={hideBackImage}
				initialRotations={[backAutoRotation, frontAutoRotation]}
			/>
		),
		[backImageForModal, backAutoRotation, frontAutoRotation]
	)

	const [showFrontTiffImage, hideFrontTiffImage] = useModal(
		() => (
			<FrontCheckImageModal
				id={id}
				status={status}
				frontImage={frontTiffImageForModal}
				backImage={backTiffImageForModal}
				closeModal={hideFrontTiffImage}
				initialRotations={[0, 0]}
			/>
		),
		[frontTiffImageForModal]
	)

	const [showBackTiffImage, hideBackTiffImage] = useModal(
		() => (
			<BackCheckImageModal
				id={id}
				status={status}
				frontImage={frontTiffImageForModal}
				backImage={backTiffImageForModal}
				closeModal={hideBackTiffImage}
				initialRotations={[0, 0]}
			/>
		),
		[backTiffImageForModal]
	)

	const showImage = () => {
		switch (side) {
			case Side.front:
				isCheckImageAvailable(activeFrontImage) && showFrontImage()
				break
			case Side.back:
				isCheckImageAvailable(activeBackImage) && showBackImage()
				break
			case Side.frontTiff:
				frontTiffImage && showFrontTiffImage()
				break
			case Side.backTiff:
				backTiffImage && showBackTiffImage()
				break
		}
	}

	const frontImageExists = [Side.front, Side.frontTiff].includes(side) && !!(activeFrontImage || frontTiffImage)
	const backImageExists = [Side.back, Side.backTiff].includes(side) && !!(activeBackImage || backTiffImage)
	const hasAnyVersionImage = printedAvailable || depositedAvailable
	const showImageControls = imageVersionMode
		? allVersionImagesSettled && hasAnyVersionImage && (frontImageExists || backImageExists)
		: frontImageExists || backImageExists
	const tiffIsSelected = [Side.frontTiff, Side.backTiff].includes(side)
	const flipToSide = [Side.front, Side.frontTiff].includes(side) ? Side.back : Side.front
	const imageObjDictionary = {
		[Side.front]: frontImageObject,
		[Side.back]: backImageObject,
		[Side.frontTiff]: frontTiffImageObject,
		[Side.backTiff]: backTiffImageObject,
	}
	const tiffImagesExists = !!frontTiffImage && !!backTiffImage

	const selectImageVersion = (
		e: React.MouseEvent<HTMLButtonElement>,
		version: CheckImageVersion,
		isAvailable: boolean
	) => {
		e.stopPropagation()
		e.preventDefault()
		if (!allVersionImagesSettled || !isAvailable || imageVersion === version) return
		setImageVersion(version)
	}
	const depositedUnavailable = allVersionImagesSettled && !depositedAvailable
	const printedUnavailable = allVersionImagesSettled && !printedAvailable

	return (
		<div className={classNames("check-image-card", {compact})}>
			<div className={classNames("check-image-card-wrapper", {"cursor-move": withZoom})}>
				<div
					className="check-image-card-container"
					data-tracking-label="open-check-image-in-full-screen"
					onClick={() => {
						if (!withZoom) {
							showImage()
						}
					}}
				>
					{(imageVersionMode || showImageControls) && (
						<>
							{imageVersionMode && (
								<div className="check-image-card-ctas-top" onClick={(e) => e.stopPropagation()}>
									<div className="check-image-card-ctas-top-container check-image-card-ctas-top-container--version">
										<button
											type="button"
											className={classNames("check-image-card-ctas-top-container-left", {
												selected: imageVersion === CheckImageVersion.Deposited,
												unavailable: depositedUnavailable,
											})}
											aria-disabled={depositedUnavailable}
											tabIndex={depositedUnavailable ? -1 : 0}
											aria-pressed={imageVersion === CheckImageVersion.Deposited}
											data-tip="React-tooltip"
											data-for={`check-image-deposited-unavailable-${id}`}
											data-tip-disable={!depositedUnavailable}
											onClick={(e) => selectImageVersion(e, CheckImageVersion.Deposited, depositedAvailable)}
										>
											<Icon icon="document-image-file" size={16} />
											<span>Deposited Check</span>
										</button>
										<button
											type="button"
											className={classNames("check-image-card-ctas-top-container-right", {
												selected: imageVersion === CheckImageVersion.Printed,
												unavailable: printedUnavailable,
											})}
											aria-disabled={printedUnavailable}
											tabIndex={printedUnavailable ? -1 : 0}
											aria-pressed={imageVersion === CheckImageVersion.Printed}
											data-tip="React-tooltip"
											data-for={`check-image-printed-unavailable-${id}`}
											data-tip-disable={!printedUnavailable}
											onClick={(e) => selectImageVersion(e, CheckImageVersion.Printed, printedAvailable)}
										>
											<Icon icon="document-image-file" size={16} />
											<span>Printed Check</span>
										</button>
									</div>
									{depositedUnavailable && (
										<ReactTooltip
											id={`check-image-deposited-unavailable-${id}`}
											effect="solid"
											className="tooltip-info"
										>
											{UNAVAILABLE_IMAGE_TOOLTIP}
										</ReactTooltip>
									)}
									{printedUnavailable && (
										<ReactTooltip id={`check-image-printed-unavailable-${id}`} effect="solid" className="tooltip-info">
											{UNAVAILABLE_IMAGE_TOOLTIP}
										</ReactTooltip>
									)}
								</div>
							)}
							{tiffImagesExists && (
								<div className="check-image-card-ctas-top">
									<div className="check-image-card-ctas-top-container">
										<div
											className={classNames("check-image-card-ctas-top-container-left", {selected: !tiffIsSelected})}
											onClick={(e) => {
												e.stopPropagation()
												if ([Side.back, Side.backTiff].includes(side)) {
													setSide(Side.back)
												} else {
													setSide(Side.front)
												}
											}}
										>
											<Icon icon="document-image-file" size={16} />
											<span>Image</span>
										</div>
										<div
											className={classNames("check-image-card-ctas-top-container-right", {selected: tiffIsSelected})}
											onClick={(e) => {
												e.stopPropagation()
												if ([Side.back, Side.backTiff].includes(side)) {
													setSide(Side.backTiff)
												} else {
													setSide(Side.frontTiff)
												}
											}}
										>
											<Icon icon="document-tiff-mage-file" size={16} />
											<span>TIFF</span>
										</div>
									</div>
								</div>
							)}
							{showImageControls && (
								<div className="check-image-card-ctas-bottom">
									<div className="check-image-card-ctas-bottom-left">
										<a
											className="single-check-deposit-link-with-icon"
											target="_blank"
											rel="noreferrer noopener"
											href=""
											onClick={(e) => {
												e.preventDefault()
												showImage()
											}}
										>
											<Icon icon="maximize-square-4--interface-essential" size={20} />
										</a>

										<div
											className="check-image-card-preview-container"
											onClick={(e) => {
												e.stopPropagation()
												changeSide()
											}}
										>
											<Icon icon="flip-element" size={20} />
											<span>{flipToSide}</span>
										</div>
									</div>

									<ImageTransformControls
										className="check-image-card-transform-controls"
										changeRotation={handleRotate}
										zoomIn={zoom.zoomIn}
										zoomOut={zoom.zoomOut}
										zoomVal={zoom.val}
									/>
								</div>
							)}
						</>
					)}
					<div className="check-image-card-image-layer">{imageObjDictionary[side]}</div>
				</div>
			</div>
		</div>
	)
}
